import { useCallback, useEffect, useRef, useState } from 'react'
import { ApiError } from '@mezha/api'
import { fromFailure, type ApiFailure } from '../api/baseApi'
import { useShellOptional } from '../shell/ShellContext'
import { explain } from './explain'

/** Runs a command: its error is kept for the screen (next to the seal) and
 *  shown in the system strip for a few seconds. A version conflict re-reads
 *  the snapshot through `onConflict`; `onError` sees every refusal. */
export function useCommand(onConflict?: () => void, onError?: (e: ApiError) => void) {
  const shell = useShellOptional()
  const [error, setError] = useState<ApiError | null>(null)
  const dropRef = useRef<symbol | null>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const clear = useCallback(() => {
    setError(null)
    if (dropRef.current && shell) shell.dropSystem(dropRef.current)
    dropRef.current = null
  }, [shell])
  useEffect(() => () => {
    if (timer.current) clearTimeout(timer.current)
    if (dropRef.current && shell) shell.dropSystem(dropRef.current)
  }, [shell])

  const run = useCallback(async <T,>(fn: () => Promise<T>): Promise<T | undefined> => {
    clear()
    try {
      return await fn()
    } catch (e) {
      const err = e instanceof ApiError ? e : fromFailure(e as ApiFailure)
      setError(err)
      if (err.code === 'version_conflict') onConflict?.()
      onError?.(err)
      if (shell) {
        dropRef.current = shell.pushSystem({ kind: 'error', text: explain(err) })
        if (timer.current) clearTimeout(timer.current)
        const id = dropRef.current
        timer.current = setTimeout(() => shell.dropSystem(id), 6000)
      }
      return undefined
    }
  }, [clear, onConflict, onError, shell])

  return { run, error, text: error ? explain(error) : null, clear }
}
